/* App.jsx — top-level router + tweaks panel */

const { useState, useEffect } = React;

// Fire voting closed notice — shows once per visitor for one week
function FireVotingClosedNotice() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const storageKey = "fire_voting_closed_notice_shown";
    const stored = localStorage.getItem(storageKey);

    if (!stored) {
      setVisible(true);
      // Mark as shown with timestamp
      localStorage.setItem(storageKey, JSON.stringify({ shown: true, date: new Date().toISOString() }));
    }
  }, []);

  if (!visible) return null;

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: 20,
      backdropFilter: "blur(2px)",
    }}>
      <div style={{
        background: "white",
        borderRadius: 20,
        padding: "40px 32px",
        maxWidth: 520,
        width: "100%",
        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
        border: "2px solid var(--c-primary-deep)",
        position: "relative",
        animation: "slideUp 300ms ease-out",
      }}>
        {/* Close button */}
        <button
          onClick={() => setVisible(false)}
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            background: "none",
            border: "none",
            fontSize: 28,
            cursor: "pointer",
            color: "var(--c-ink-soft)",
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            transition: "background 120ms",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "var(--c-cream-soft)"}
          onMouseLeave={e => e.currentTarget.style.background = "none"}
          aria-label="Close notice"
        >
          ×
        </button>

        {/* Fire logo */}
        <div style={{
          width: 80,
          height: 80,
          borderRadius: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 24px",
          boxShadow: "0 8px 24px rgba(216, 85, 43, 0.25)",
          overflow: "hidden",
        }}>
          <img src="/img/fire-logo.jpg" alt="Fire Prevention Play" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 16 }} />
        </div>

        {/* Content */}
        <h2 style={{
          fontSize: "clamp(22px, 5vw, 28px)",
          fontWeight: 700,
          margin: "0 0 12px 0",
          textAlign: "center",
          color: "var(--c-primary-deep)",
          fontFamily: "var(--f-display)",
        }}>
          Though the curtain has closed
        </h2>

        <p style={{
          fontSize: 16,
          lineHeight: 1.7,
          color: "var(--c-ink)",
          textAlign: "center",
          margin: "0 0 24px 0",
        }}>
          The performances live on! Check out all the amazing Fire Prevention Play videos from Philadelphia Parks & Recreation below.
        </p>

        {/* CTA Button */}
        <a
          href="#fireplay"
          onClick={() => setVisible(false)}
          style={{
            display: "inline-flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
            padding: "14px 24px",
            background: "var(--c-primary)",
            color: "white",
            textDecoration: "none",
            borderRadius: 12,
            fontWeight: 700,
            fontSize: 15,
            fontFamily: "var(--f-display)",
            border: "none",
            cursor: "pointer",
            transition: "all 120ms",
            boxShadow: "0 4px 12px rgba(216, 85, 43, 0.25)",
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 8px 20px rgba(216, 85, 43, 0.35)";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = "";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(216, 85, 43, 0.25)";
          }}
        >
          Watch the videos ↓
        </a>

        <style>{`
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    </div>
  );
}

// Wonderland ticket popup — Young Performers Theatre Camp, Aug 6 + 7 2026.
// Home page only, once per page load (not localStorage-gated: JJ wants it in
// front of every visitor while the shows are selling). Self-retires after the
// last performance so nobody has to remember to pull it down.
// URL + retire date live in data.js so the gallery's Wonderland card shares them.
const WONDERLAND_TICKETS_URL = (window.WONDERLAND || {}).ticketsUrl;
const WONDERLAND_LAST_SHOW = new Date((window.WONDERLAND || {}).lastShow);
let wonderlandNoticeSeenThisLoad = false;

function WonderlandTicketsNotice() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (wonderlandNoticeSeenThisLoad) return;
    if (new Date() >= WONDERLAND_LAST_SHOW) return;
    wonderlandNoticeSeenThisLoad = true;
    setVisible(true);
  }, []);

  // Esc closes, and the page behind stays put while the modal is open.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => { if (e.key === "Escape") setVisible(false); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      onClick={() => setVisible(false)}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
        backdropFilter: "blur(2px)",
        overflowY: "auto",
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Wonderland tickets"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: 18,
          maxWidth: 340,
          width: "100%",
          margin: "auto",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.28)",
          border: "2px solid var(--c-primary-deep)",
          position: "relative",
          overflow: "hidden",
          animation: "slideUp 300ms ease-out",
        }}
      >
        <button
          onClick={() => setVisible(false)}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "rgba(255, 255, 255, 0.9)",
            border: "none",
            fontSize: 26,
            lineHeight: 1,
            cursor: "pointer",
            color: "var(--c-ink)",
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            transition: "background 120ms",
            zIndex: 2,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--c-cream-soft)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)")}
          aria-label="Close"
        >
          ×
        </button>

        <a href={WONDERLAND_TICKETS_URL} target="_blank" rel="noopener noreferrer" style={{ display: "block" }}>
          <img
            src="/img/wonderland-2026.jpg"
            alt="Young Performers Theater Camp presents Wonderland. August 6 and 7, 6:30pm. Tickets $10. Venice Island Performing Arts Center, 7 Lock Street, Philadelphia PA."
            style={{
              display: "block",
              width: "100%",
              maxHeight: "42vh",
              objectFit: "contain",
              background: "var(--c-cream-soft)",
            }}
          />
        </a>

        <div style={{ padding: "16px 20px 20px" }}>
          <p style={{
            fontSize: 11,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "var(--c-primary)",
            margin: "0 0 5px 0",
            textAlign: "center",
            fontFamily: "var(--f-display)",
          }}>
            August 6 &amp; 7 · 6:30pm · $10
          </p>

          <h2 style={{
            fontSize: "clamp(20px, 5vw, 24px)",
            fontWeight: 800,
            margin: "0 0 8px 0",
            textAlign: "center",
            color: "var(--c-primary-deep)",
            fontFamily: "var(--f-display)",
            lineHeight: 1.15,
          }}>
            Wonderland
          </h2>

          <p style={{
            fontSize: 13.5,
            lineHeight: 1.55,
            color: "var(--c-ink)",
            textAlign: "center",
            margin: "0 0 16px 0",
            // The modal is 340px wide, so this paragraph was breaking with
            // "built." alone on the last line. `balance` evens the lines out and
            // the nbsp guarantees the last two words stay together wherever a
            // browser does not support it.
            textWrap: "balance",
          }}>
            Our Young Performers Theater Camp takes the stage for two nights only,
            at the Venice Island Performing Arts Center. Come see what they&nbsp;built.
          </p>

          <a
            href={WONDERLAND_TICKETS_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
              padding: "12px 20px",
              background: "var(--c-primary)",
              color: "white",
              textDecoration: "none",
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 15,
              fontFamily: "var(--f-display)",
              border: "none",
              cursor: "pointer",
              transition: "all 120ms",
              boxShadow: "0 4px 12px rgba(216, 85, 43, 0.25)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 20px rgba(216, 85, 43, 0.35)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(216, 85, 43, 0.25)";
            }}
          >
            Get tickets →
          </a>

          {/* Secondary on purpose -- the popup's job is still selling the two
              nights. Deep links into the gallery's Wonderland set (the 50
              rehearsal + dress-run shots), and closes the modal on the way so
              the visitor is not left dismissing a dialog over the photos they
              just asked for. Plain href, so it still works if the click
              handler never fires. */}
          <a
            href="#gallery?set=wonderland"
            onClick={() => setVisible(false)}
            style={{
              display: "inline-flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              marginTop: 10,
              padding: "10px 16px",
              background: "none",
              color: "var(--c-primary-deep)",
              textDecoration: "none",
              borderRadius: 10,
              border: "2px solid var(--c-primary-deep)",
              fontWeight: 700,
              fontSize: 13.5,
              fontFamily: "var(--f-display)",
              cursor: "pointer",
              transition: "background 120ms",
              textAlign: "center",
              lineHeight: 1.25,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--c-cream-soft)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
          >
            Behind the Scenes Photos →
          </a>

          <button
            onClick={() => setVisible(false)}
            style={{
              display: "block",
              width: "100%",
              marginTop: 8,
              padding: "6px",
              background: "none",
              border: "none",
              color: "var(--c-ink-soft)",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Maybe later
          </button>
        </div>

        <style>{`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    </div>
  );
}

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "palette": "default",
  "typePair": "grotesque"
}/*EDITMODE-END*/;

// CR #339 (CR-30243): "firecamp" route renamed to "fireplay" per JJ. Old
// shared #firecamp links still resolve via the alias below (same mechanism as
// "submit" -> "promote").
const VALID_ROUTES = ["home", "camps", "programs", "gallery", "venice", "mece-choir", "about", "collaboration", "testimonials", "employment", "employment-apply", "promote", "workshop", "register", "volunteer", "attendance", "fireplay", "openmic", "interest", "holidayshow"];
const routeAlias = (h) => h === "submit" ? "promote" : h === "firecamp" ? "fireplay" : h;

// Summer Camp Showcase popup — 8/10/26 at the Dell Music Center. Home page
// only. Aimed at the audience sitting in that room: the slideshow of Intro Camp
// photos runs during the show, and this is how someone who just watched it gets
// to the full set. Config (retire date + target) lives in data.js.
//
// Unlike the Wonderland ticket popup, this one is localStorage-gated to once
// per visitor. That popup was selling tickets and JJ wanted it in front of
// everyone every load; this one answers a question the visitor only has once,
// and a modal that reappears on every tap is worse than useless to someone
// trying to browse photos on a phone in a dark amphitheatre.
const CAMP_SHOWCASE_SEEN_KEY = "ppr.campShowcaseNotice.seen";

function CampShowcasePhotosNotice() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const cfg = window.CAMP_SHOWCASE || {};
    if (!cfg.endsAt || new Date() >= new Date(cfg.endsAt)) return;
    // Safari private mode throws on localStorage. A storage failure must not
    // cost the visitor the popup, so a throw falls through to showing it.
    try {
      if (window.localStorage.getItem(CAMP_SHOWCASE_SEEN_KEY)) return;
    } catch (e) { /* no storage -- show it */ }
    setVisible(true);
  }, []);

  const dismiss = React.useCallback(() => {
    setVisible(false);
    try { window.localStorage.setItem(CAMP_SHOWCASE_SEEN_KEY, "1"); } catch (e) { /* nothing to do */ }
  }, []);

  // Esc closes, and the page behind stays put while the modal is open.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [visible, dismiss]);

  if (!visible) return null;

  const photosUrl = (window.CAMP_SHOWCASE || {}).photosUrl || "#gallery?set=introcamps";

  return (
    <div
      onClick={dismiss}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
        backdropFilter: "blur(2px)",
        overflowY: "auto",
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Summer Camp Showcase photos"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: 18,
          maxWidth: 340,
          width: "100%",
          margin: "auto",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.28)",
          border: "2px solid var(--c-primary-deep)",
          position: "relative",
          overflow: "hidden",
          animation: "slideUp 300ms ease-out",
        }}
      >
        <button
          onClick={dismiss}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "rgba(255, 255, 255, 0.9)",
            border: "none",
            fontSize: 26,
            lineHeight: 1,
            cursor: "pointer",
            color: "var(--c-ink)",
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            transition: "background 120ms",
            zIndex: 2,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--c-cream-soft)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)")}
          aria-label="Close"
        >
          ×
        </button>

        {/* The Dell's own wordmark is a white knockout (fill:#fff throughout),
            so it needs a dark ground -- #002559 is the navy off their own site,
            not a colour invented for this. The band doubles as the venue line,
            which is why the date strip below no longer repeats "The Dell Music
            Center". Asset is served locally from /img; never hotlinked. */}
        <div style={{
          background: "#002559",
          padding: "18px 20px 16px",
          display: "flex",
          justifyContent: "center",
        }}>
          <img
            src="/img/dell-music-center-logo.svg"
            alt="The Dell Music Center"
            style={{ display: "block", width: 96, height: "auto" }}
          />
        </div>

        <div style={{ padding: "18px 20px 20px" }}>
          <p style={{
            fontSize: 11,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "var(--c-primary)",
            margin: "0 0 8px 0",
            textAlign: "center",
            fontFamily: "var(--f-display)",
          }}>
            {/* Not "Tonight" -- this popup is live the day before the show too,
                and a visitor reading it on the 9th would be told the wrong day.
                The date is the one thing here that is true whenever it shows. */}
            August 10 · 6:30PM
          </p>

          <h2 style={{
            fontSize: "clamp(22px, 5.5vw, 27px)",
            fontWeight: 800,
            margin: "0 0 10px 0",
            textAlign: "center",
            color: "var(--c-primary-deep)",
            fontFamily: "var(--f-display)",
            lineHeight: 1.1,
          }}>
            HEY! At the Summer Camp Showcase?
          </h2>

          <p style={{
            fontSize: 13.5,
            lineHeight: 1.55,
            color: "var(--c-ink)",
            textAlign: "center",
            margin: "0 0 16px 0",
            textWrap: "balance",
          }}>
            The photos from the slideshow are all here. Browse the 2026 Intro
            Camps and pick your rec center.
          </p>

          {/* Plain href, so it still works if the click handler never fires.
              Dismissing on the way out matters more here than it does on the
              Wonderland popup: this one is localStorage-gated, and a visitor who
              taps through to the photos has plainly answered the question, so
              it should not be sitting there waiting on their next visit. */}
          <a
            href={photosUrl}
            onClick={dismiss}
            style={{
              display: "inline-flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
              padding: "12px 20px",
              background: "var(--c-primary)",
              color: "white",
              textDecoration: "none",
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 15,
              fontFamily: "var(--f-display)",
              border: "none",
              cursor: "pointer",
              transition: "all 120ms",
              boxShadow: "0 4px 12px rgba(216, 85, 43, 0.25)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 20px rgba(216, 85, 43, 0.35)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(216, 85, 43, 0.25)";
            }}
          >
            See the photos →
          </a>
        </div>
      </div>
    </div>
  );
}

// PPR is hiring -- the full-time Recreation Leader Trainee posting on the
// City's jobs board. Config (URL, deadline, storage key) lives in data.js.
//
// ONCE PER VISITOR, and that is the whole brief: this is for someone arriving
// at the site for the first time, not a banner for the regulars. Same
// localStorage gate as CampShowcasePhotosNotice for the same reason -- a modal
// that reappears on every tap is worse than useless to someone browsing photos
// on a phone. The key carries the posting id, so the NEXT posting reaches
// people who dismissed this one; see data.js.
//
// It self-retires at `closesAt` rather than waiting for someone to remember to
// pull it down, because a popup that outlives its own application deadline
// sends people to a closed listing.
//
// NOT home-page only, unlike the other two notices. A first-time visitor lands
// wherever search sent them, and a hiring notice that only fires on / misses
// most of them. The two employment routes are the exception: the visitor is
// already looking at the jobs, and a modal over that page is just in the way.
const HIRING_SUPPRESSED_ROUTES = ["employment", "employment-apply"];

// Belt to localStorage's braces. The effect below re-runs on every route
// change, which is what lets someone who landed on the Employment page still
// meet the popup once they browse on -- but it also means that in Safari
// private mode, where the write silently fails, a dismissed popup would come
// straight back on the next click. This flag is the one thing that cannot
// fail, and it is per page load on purpose: clearing storage is how you get
// the popup back for testing, and a reload is part of that.
let hiringNoticeDoneThisLoad = false;

function HiringNotice({ route }) {
  const cfg = window.PPR_HIRING || {};
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (hiringNoticeDoneThisLoad) return;
    if (!cfg.url || !cfg.closesAt) return;
    // Fails closed on an unparseable date -- see pprNoticeIsLive in data.js
    // for why that matters more than it looks like it should.
    if (!window.pprNoticeIsLive(cfg.closesAt, Date.now())) return;
    if (HIRING_SUPPRESSED_ROUTES.includes(route)) return;
    // Safari private mode throws on localStorage. A storage failure must not
    // cost the visitor the popup, so a throw falls through to showing it.
    try {
      if (cfg.seenKey && window.localStorage.getItem(cfg.seenKey)) return;
    } catch (e) { /* no storage -- show it */ }
    setVisible(true);
  }, [route]);

  const dismiss = React.useCallback(() => {
    hiringNoticeDoneThisLoad = true;
    setVisible(false);
    try {
      if (cfg.seenKey) window.localStorage.setItem(cfg.seenKey, "1");
    } catch (e) { /* nothing to do */ }
  }, [cfg.seenKey]);

  // Esc closes, and the page behind stays put while the modal is open.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [visible, dismiss]);

  if (!visible) return null;

  return (
    <div
      onClick={dismiss}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
        backdropFilter: "blur(2px)",
        overflowY: "auto",
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Philadelphia Parks &amp; Recreation is hiring"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: 18,
          maxWidth: 340,
          width: "100%",
          margin: "auto",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.28)",
          border: "2px solid var(--c-primary-deep)",
          position: "relative",
          overflow: "hidden",
          animation: "slideUp 300ms ease-out",
        }}
      >
        <button
          onClick={dismiss}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "rgba(255, 255, 255, 0.9)",
            border: "none",
            fontSize: 26,
            lineHeight: 1,
            cursor: "pointer",
            color: "var(--c-ink)",
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            transition: "background 120ms",
            zIndex: 2,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--c-cream-soft)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)")}
          aria-label="Close"
        >
          ×
        </button>

        {/* The Employment page's own hero photo. Decorative here -- the heading
            below carries the whole message -- so it takes an empty alt rather
            than repeating it to a screen reader. */}
        <img
          src="/img/employment-work-with-ppr.jpg"
          alt=""
          style={{
            display: "block",
            width: "100%",
            height: 160,
            objectFit: "cover",
            // The subject's head sits high in the source frame, so a centred
            // crop clips it. Pulls the window up just enough to keep headroom.
            objectPosition: "center 35%",
            background: "var(--c-cream-soft)",
          }}
        />

        <div style={{ padding: "18px 20px 20px" }}>
          <p style={{
            fontSize: 11,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "var(--c-primary)",
            margin: "0 0 8px 0",
            textAlign: "center",
            fontFamily: "var(--f-display)",
          }}>
            Now hiring
          </p>

          <h2 style={{
            fontSize: "clamp(21px, 5.2vw, 26px)",
            fontWeight: 800,
            margin: "0 0 10px 0",
            textAlign: "center",
            color: "var(--c-primary-deep)",
            fontFamily: "var(--f-display)",
            lineHeight: 1.15,
          }}>
            Philadelphia Parks &amp; Recreation is hiring full-time staff.
          </h2>

          <p style={{
            fontSize: 13.5,
            lineHeight: 1.55,
            color: "var(--c-ink)",
            textAlign: "center",
            margin: "0 0 6px 0",
            textWrap: "balance",
          }}>
            {cfg.title} &mdash; an entry-level City position leading recreation
            programs in neighborhoods across Philadelphia.
          </p>

          {cfg.detail && (
            <p style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: "var(--c-ink-soft)",
              textAlign: "center",
              margin: "0 0 16px 0",
              fontFamily: "var(--f-display)",
            }}>
              {cfg.detail}
            </p>
          )}

          {/* Plain href, so it still works if the click handler never fires.
              Dismissing on the way out matters here for the same reason it does
              on the showcase popup: someone who has opened the listing has
              answered the question, and the modal should not be waiting for
              them when they come back. Opens in a new tab -- the listing is on
              the City's jobs board, not this site, and a visitor mid-browse
              should keep the page they were on. */}
          <a
            href={cfg.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={dismiss}
            style={{
              display: "inline-flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
              padding: "12px 20px",
              background: "var(--c-primary)",
              color: "white",
              textDecoration: "none",
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 15,
              fontFamily: "var(--f-display)",
              border: "none",
              cursor: "pointer",
              transition: "all 120ms",
              boxShadow: "0 4px 12px rgba(216, 85, 43, 0.25)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 20px rgba(216, 85, 43, 0.35)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(216, 85, 43, 0.25)";
            }}
          >
            See the job posting &rarr;
          </a>

          <button
            onClick={dismiss}
            style={{
              display: "block",
              width: "100%",
              marginTop: 8,
              padding: "6px",
              background: "none",
              border: "none",
              color: "var(--c-ink-soft)",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            No thanks
          </button>
        </div>

        <style>{`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    </div>
  );
}


// HARVEST OPEN MIC NIGHT -- the fall community open mic. Config (name, date,
// time, venue, retire date, storage key) lives in data.js, shared with the
// sign-up page and the footer link, so the night moves in exactly one place.
//
// THE ONE POPUP HERE THAT KEEPS THE VISITOR ON THIS SITE. Wonderland sold
// tickets on ticketleap, the hiring notice opens the City's jobs board; this
// one has a button that routes to /#openmic, which means it must NOT be a
// plain target="_blank" anchor copied from those. It calls setRoute, and the
// href is "#openmic" so a middle-click or a blocked handler still lands
// somewhere real.
//
// ONCE PER VISITOR, same localStorage gate and the same two belts as
// HiringNotice, for the same reasons: a modal that returns on every tap is
// worse than useless on a phone, and the Safari-private-mode write is a
// silent no-op, so the per-load flag is the half that cannot fail.
//
// NOT home-page only. Someone who arrived from a search for camp photos is
// exactly who this is for. The sign-up page is suppressed -- they are already
// looking at the form. So is the participant registration form: families land
// there straight from the Dance@Athletic invite link, and a modal about a
// different event is in the way of the one thing they came to do.
const OPEN_MIC_SUPPRESSED_ROUTES = ["openmic", "register"];

let openMicNoticeDoneThisLoad = false;

function OpenMicNotice({ route, setRoute }) {
  const cfg = window.PPR_OPEN_MIC || {};
  // Sign-ups closed (data.js): the popup stops asking people to perform and
  // just invites them to come -- no sign-up button, no link off to the form.
  const signupsClosed = cfg.signupsClosed === true;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (openMicNoticeDoneThisLoad) return;
    // popup:false in data.js turns the modal off WITHOUT retiring the event --
    // the sign-up page, the nav link and the footer link all read the same
    // cfg and stay live off cfg.route/closesAt, so moving the night's date is
    // still one edit. JJ asked for the popup down on 2026-10-21 while the
    // 10/26 night was still upcoming.
    if (cfg.popup === false) return;
    if (!cfg.closesAt || !cfg.route) return;
    // Fails closed on an unparseable date -- see pprNoticeIsLive in data.js.
    if (!window.pprNoticeIsLive(cfg.closesAt, Date.now())) return;
    if (OPEN_MIC_SUPPRESSED_ROUTES.includes(route)) return;
    // ONCE A DAY, not once ever. The gate lives in data.js so the clock and
    // the storage can be driven from a test; it stores the day, not a flag.
    if (window.pprNoticeSeenToday(cfg.seenKey, Date.now())) return;
    setVisible(true);
  }, [route]);

  const dismiss = React.useCallback(() => {
    openMicNoticeDoneThisLoad = true;
    setVisible(false);
    window.pprMarkNoticeSeen(cfg.seenKey, Date.now());
  }, [cfg.seenKey]);

  // Routing to the sign-up page counts as answering the question, so it
  // dismisses on the way. preventDefault keeps the hash change ours rather
  // than the browser's, which is what stops the page jumping before the
  // route state has caught up.
  const goSignUp = React.useCallback((e) => {
    if (e) e.preventDefault();
    dismiss();
    if (setRoute) setRoute(cfg.route);
    else window.location.hash = `#${cfg.route}`;
  }, [dismiss, setRoute, cfg.route]);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [visible, dismiss]);

  if (!visible) return null;

  return (
    <div
      onClick={dismiss}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
        backdropFilter: "blur(2px)",
        overflowY: "auto",
      }}
      role="dialog"
      aria-modal="true"
      aria-label={cfg.name || "Open Mic Night"}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: 18,
          maxWidth: 360,
          width: "100%",
          margin: "auto",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.28)",
          border: "2px solid var(--c-primary-deep)",
          position: "relative",
          overflow: "hidden",
          animation: "slideUp 300ms ease-out",
        }}
      >
        <button
          onClick={dismiss}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "var(--c-paper)",
            border: "2px solid var(--c-ink)",
            lineHeight: 0,
            cursor: "pointer",
            color: "var(--c-ink)",
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            transition: "background 120ms",
            zIndex: 2,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--c-cream-soft)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "var(--c-paper)")}
          aria-label="Close"
        >
          {/* SVG, not a glyph -- house icon standard. aria-hidden because the
              button above already carries the accessible name. */}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
            <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* THE POSTER'S OWN ARTWORK, cut from the flyer PDF above its date
            band -- the date, venue and CTA are real HTML below, so baking them
            into the image would say everything twice and say it to a screen
            reader not at all. Someone who saw this on a wall should recognise
            the modal instantly, which no stock photo of a stage would do.
            Natural aspect rather than a fixed height with objectFit:cover: it
            is a portrait poster, and a letterbox crop cuts the title off
            centre. Decorative -- the heading below carries the whole message
            -- so an empty alt. */}
        {cfg.art && (
          <img
            src={cfg.art}
            alt=""
            style={{
              display: "block",
              width: "100%",
              height: "auto",
              background: "var(--c-cream-soft)",
            }}
          />
        )}

        <div style={{ padding: "18px 20px 20px" }}>
          <p style={{
            fontSize: 11,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "var(--c-primary)",
            margin: "0 0 8px 0",
            textAlign: "center",
            fontFamily: "var(--f-display)",
          }}>
            {signupsClosed ? "You're invited" : "Sign-ups are open"}
          </p>

          <h2 style={{
            fontSize: "clamp(21px, 5.2vw, 26px)",
            fontWeight: 800,
            margin: "0 0 10px 0",
            textAlign: "center",
            color: "var(--c-primary-deep)",
            fontFamily: "var(--f-display)",
            lineHeight: 1.15,
          }}>
            {cfg.name || "Open Mic Night"}
          </h2>

          <p style={{
            fontSize: 13.5,
            lineHeight: 1.55,
            color: "var(--c-ink)",
            textAlign: "center",
            margin: "0 0 6px 0",
            textWrap: "balance",
          }}>
            {signupsClosed
              ? "A night of music, singing, poetry, dance and comedy from your neighbors. Come on out and watch."
              : "Musicians, singers, rappers, poets, dancers, comedians — bring five minutes of whatever you do."}
          </p>

          {(cfg.date || cfg.venueName) && (
            <p style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: "var(--c-ink-soft)",
              textAlign: "center",
              margin: "0 0 4px 0",
              fontFamily: "var(--f-display)",
            }}>
              {[cfg.date, cfg.time, cfg.venueName].filter(Boolean).join(" · ")}
            </p>
          )}

          {/* The age range decides whether the reader is eligible at all, so it
              goes on the modal rather than waiting on the form. */}
          {cfg.audience && (
            <p style={{
              fontSize: 12,
              color: "var(--c-ink-soft)",
              textAlign: "center",
              margin: "0 0 16px 0",
            }}>
              {cfg.audience}
            </p>
          )}

          {signupsClosed ? (
            // No sign-up anymore -- the only action is to acknowledge and go.
            <button
              onClick={dismiss}
              style={{
                display: "block",
                width: "100%",
                padding: "12px 20px",
                background: "var(--c-primary)",
                color: "white",
                borderRadius: 10,
                fontWeight: 700,
                fontSize: 15,
                fontFamily: "var(--f-display)",
                border: "none",
                cursor: "pointer",
                transition: "all 120ms",
                boxShadow: "0 4px 12px rgba(216, 85, 43, 0.25)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 20px rgba(216, 85, 43, 0.35)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(216, 85, 43, 0.25)";
              }}
            >
              See you there
            </button>
          ) : (
            <React.Fragment>
              <a
                href={`#${cfg.route}`}
                onClick={goSignUp}
                style={{
                  display: "inline-flex",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "12px 20px",
                  background: "var(--c-primary)",
                  color: "white",
                  textDecoration: "none",
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 15,
                  fontFamily: "var(--f-display)",
                  border: "none",
                  cursor: "pointer",
                  transition: "all 120ms",
                  boxShadow: "0 4px 12px rgba(216, 85, 43, 0.25)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 8px 20px rgba(216, 85, 43, 0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(216, 85, 43, 0.25)";
                }}
              >
                Sign up to perform
              </a>

              <button
                onClick={dismiss}
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 8,
                  padding: "6px",
                  background: "none",
                  border: "none",
                  color: "var(--c-ink-soft)",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Maybe next time
              </button>
            </React.Fragment>
          )}
        </div>

        <style>{`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    </div>
  );
}

// "You Are Mine" ticket popup -- an original musical at Venice Island,
// Nov 20 + 21 2026. Config lives in data.js (PPR_YOU_ARE_MINE). Same
// once-a-day gate as the open mic: stores the dismiss day, not a flag, so
// it comes back tomorrow but never twice in one visit. Self-retires after
// the run via closesAt. Not route-gated (same logic as OpenMicNotice).
let youAreMineNoticeDoneThisLoad = false;

function YouAreMineNotice() {
  const cfg = window.PPR_YOU_ARE_MINE || {};
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (youAreMineNoticeDoneThisLoad) return;
    if (cfg.popup === false) return;
    if (!cfg.closesAt || !cfg.ticketsUrl) return;
    if (!window.pprNoticeIsLive(cfg.closesAt, Date.now())) return;
    if (window.pprNoticeSeenToday(cfg.seenKey, Date.now())) return;
    setVisible(true);
  }, []);

  const dismiss = React.useCallback(() => {
    youAreMineNoticeDoneThisLoad = true;
    setVisible(false);
    window.pprMarkNoticeSeen(cfg.seenKey, Date.now());
  }, [cfg.seenKey]);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [visible, dismiss]);

  if (!visible) return null;

  return (
    <div
      onClick={dismiss}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
        backdropFilter: "blur(3px)",
        overflowY: "auto",
      }}
      role="dialog"
      aria-modal="true"
      aria-label="You Are Mine musical"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--c-ink)",
          borderRadius: 20,
          maxWidth: 400,
          width: "100%",
          margin: "auto",
          boxShadow: "0 24px 80px rgba(0, 0, 0, 0.45)",
          border: "2px solid var(--c-accent)",
          position: "relative",
          overflow: "hidden",
          animation: "slideUp 350ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <button
          onClick={dismiss}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "rgba(0, 0, 0, 0.5)",
            border: "2px solid rgba(255, 255, 255, 0.3)",
            lineHeight: 0,
            cursor: "pointer",
            color: "white",
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            transition: "background 120ms",
            zIndex: 2,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0, 0, 0, 0.7)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(0, 0, 0, 0.5)")}
          aria-label="Close"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
            <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {cfg.art && (
          <div style={{ padding: "24px 24px 0", display: "flex", justifyContent: "center" }}>
            <img
              src={cfg.art}
              alt=""
              style={{
                display: "block",
                width: "55%",
                height: "auto",
                borderRadius: 10,
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
              }}
            />
          </div>
        )}

        <div style={{ padding: "22px 24px 24px" }}>
          <p style={{
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "var(--c-accent)",
            margin: "0 0 8px 0",
            textAlign: "center",
            fontFamily: "var(--f-display)",
          }}>
            {cfg.dates} {cfg.time && ("· " + cfg.time)}
          </p>

          <h2 style={{
            fontSize: "clamp(26px, 6vw, 34px)",
            fontWeight: 800,
            margin: "0 0 8px 0",
            textAlign: "center",
            color: "var(--c-cream)",
            fontFamily: "var(--f-accent)",
            lineHeight: 1.1,
            fontStyle: "italic",
          }}>
            {cfg.name}
          </h2>

          <p style={{
            fontSize: 14,
            lineHeight: 1.6,
            color: "var(--c-cream)",
            textAlign: "center",
            margin: "0 0 6px 0",
            textWrap: "balance",
          }}>
            {cfg.tagline}
          </p>

          {cfg.venueName && (
            <p style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: "var(--c-accent)",
              textAlign: "center",
              margin: "0 0 4px 0",
              fontFamily: "var(--f-display)",
            }}>
              {cfg.venueName} Performing Arts Center
            </p>
          )}

          {cfg.credits && (
            <p style={{
              fontSize: 11.5,
              color: "rgba(255, 255, 255, 0.5)",
              textAlign: "center",
              margin: "0 0 18px 0",
              lineHeight: 1.5,
            }}>
              {cfg.credits.split(" · ").map(function (line, i, arr) {
                return React.createElement(React.Fragment, { key: i },
                  line, i < arr.length - 1 ? React.createElement("br") : null
                );
              })}
            </p>
          )}

          <a
            href={cfg.ticketsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => { dismiss(); }}
            style={{
              display: "inline-flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
              padding: "14px 24px",
              background: "var(--c-primary)",
              color: "white",
              textDecoration: "none",
              borderRadius: 12,
              fontWeight: 700,
              fontSize: 16,
              fontFamily: "var(--f-display)",
              border: "none",
              cursor: "pointer",
              transition: "all 150ms",
              boxShadow: "0 4px 16px rgba(216, 85, 43, 0.35)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(216, 85, 43, 0.45)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "";
              e.currentTarget.style.boxShadow = "0 4px 16px rgba(216, 85, 43, 0.35)";
            }}
          >
            Get Tickets
          </a>

          <button
            onClick={dismiss}
            style={{
              display: "block",
              width: "100%",
              marginTop: 10,
              padding: "6px",
              background: "none",
              border: "none",
              color: "rgba(255, 255, 255, 0.4)",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Maybe later
          </button>
        </div>

        <style>{`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(24px) scale(0.97); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>
      </div>
    </div>
  );
}

function App() {
  // Split route from any query string. The shop's Stripe redirect was the
  // first thing to need this (CR #264, retired 2026-08-14) and the gallery's
  // /#gallery?set=... deep links still do.
  const rawHash = (window.location.hash || "#home").slice(1) || "home";
  const initialHash = routeAlias(rawHash.split("?")[0]);
  const [route, setRoute] = useState(VALID_ROUTES.includes(initialHash) ? initialHash : "home");
  const [tweaks, setTweak] = window.useTweaks ? window.useTweaks(TWEAK_DEFAULTS) : [TWEAK_DEFAULTS, () => {}];

  useEffect(() => {
    // Only rewrite the hash if the route part actually changed -- preserves
    // any query string the route currently carries (the gallery's ?set=/&loc=).
    const currentRoutePart = (window.location.hash || "").slice(1).split("?")[0];
    if (currentRoutePart !== route) {
      history.replaceState(null, "", `#${route}`);
    }
    // CR #204: scroll to top on every route change so users land at the top
    // of the new page (footer link clicks were preserving the bottom-of-page
    // scroll position from the previous page).
    window.scrollTo(0, 0);
  }, [route]);

  useEffect(() => {
    const onPop = () => {
      let h = ((window.location.hash || "#home").slice(1) || "home").split("?")[0];
      h = routeAlias(h);
      // Only react to recognised top-level routes. In-page anchors like #sites
      // or #process should fall through so the browser scrolls them into view.
      if (VALID_ROUTES.includes(h)) setRoute(h);
    };
    window.addEventListener("hashchange", onPop);
    return () => window.removeEventListener("hashchange", onPop);
  }, []);

  // Apply tweaks to root
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.palette = tweaks.palette === "default" ? "" : tweaks.palette;
    root.dataset.type = tweaks.typePair;
  }, [tweaks.palette, tweaks.typePair]);

  return (
    <div className="site">
      {/* Both are home-page only and both self-retire, so they cannot stack:
          Wonderland's window closed 8/8, the showcase's opens through 8/10. */}
      {route === "home" && <WonderlandTicketsNotice />}
      {route === "home" && <CampShowcasePhotosNotice />}
      {route === "fireplay" && <FireVotingClosedNotice />}
      {/* Not gated on the route -- see HiringNotice, which does its own
          suppression on the two employment pages. It cannot stack with the two
          above: both of those retired in August 2026, and this one is mounted
          on every route they are not. */}
      <HiringNotice route={route} />
      {/* Same reasoning as HiringNotice: somebody already on the sign-up page
          does not need a modal telling them to go to it, and the registration
          form is kept clear too. See OpenMicNotice. */}
      <OpenMicNotice route={route} setRoute={setRoute} />
      <YouAreMineNotice />
      <Header route={route} setRoute={setRoute} />
      <main>
        {route === "home" && <HomePage setRoute={setRoute} />}
        {route === "camps" && <CampsPage setRoute={setRoute} />}
        {route === "programs" && <ProgramsPage />}
        {route === "gallery" && <GalleryPage />}
        {route === "venice" && <VenicePage />}
        {route === "mece-choir" && <MECEChoirPage />}
        {route === "about" && <AboutPage />}
        {route === "collaboration" && <CollaborationPage />}
        {route === "testimonials" && <TestimonialsPage />}
        {route === "employment" && <EmploymentPage />}
        {route === "employment-apply" && <EmploymentApplicationPage />}
        {route === "promote" && <CenterSubmitPage />}
        {route === "workshop" && <WorkshopPage />}
        {route === "register" && <ParticipantRegistrationPage />}
        {route === "volunteer" && <VolunteerPage />}
        {route === "attendance" && <ParentAttendancePage />}
        {route === "fireplay" && <FirecampPage />}
        {route === "openmic" && <OpenMicPage />}
        {route === "interest" && <InterestCheckPage />}
        {route === "holidayshow" && <HolidayShowPage />}
      </main>
      <Footer setRoute={setRoute} />

      {window.TweaksPanel && (
        <window.TweaksPanel title="Tweaks">
          <window.TweakSection title="Color palette">
            <window.TweakRadio
              value={tweaks.palette}
              onChange={(v) => setTweak("palette", v)}
              options={[
                { value: "default", label: "Sunlit Terracotta" },
                { value: "plum", label: "Stage Plum" },
                { value: "citrus", label: "Daylight Citrus" },
                { value: "cobalt", label: "Civic Cobalt" },
              ]}
            />
          </window.TweakSection>
          <window.TweakSection title="Typography">
            <window.TweakRadio
              value={tweaks.typePair}
              onChange={(v) => setTweak("typePair", v)}
              options={[
                { value: "grotesque", label: "Bricolage + DM Sans" },
                { value: "condensed", label: "Familjen Grotesk" },
                { value: "editorial", label: "Fraunces + DM Sans" },
                { value: "poster", label: "Anton + DM Sans" },
              ]}
            />
          </window.TweakSection>
        </window.TweaksPanel>
      )}
    </div>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(<App />);
